import React, { useMemo } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

interface SimpleWysiwygEditorProps {
  value?: string;
  onChange?: (content: string) => void;
  placeholder?: string;
}

/**
 * 简单所见即所得编辑器。
 *
 * 仅提供常见问题文案需要的基础排版能力，避免用户直接编辑 HTML。
 */
const SimpleWysiwygEditor: React.FC<SimpleWysiwygEditorProps> = ({
  value,
  onChange,
  placeholder,
}) => {
  /**
   * 编辑器工具栏配置。
   */
  const modules = useMemo(() => ({
    toolbar: [
      [{ header: [2, 3, 4, 5, false] }],
      ['bold', 'italic', 'underline'],
      [{ color: [] }, { background: [] }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['blockquote', 'link'],
      ['clean'],
    ],
    // 避免 Quill 在把现有 HTML 重新转换为内部 Delta 时，按视觉间距额外补空行。
    clipboard: {
      matchVisual: false,
    },
  }), []);

  /**
   * 允许输出的富文本格式。
   */
  const formats = useMemo(() => [
    'header',
    'bold',
    'italic',
    'underline',
    'color',
    'background',
    'list',
    'bullet',
    'blockquote',
    'link',
  ], []);

  /**
   * 将 Quill 的空内容归一化为空字符串，便于后端和前端统一判断。
   */
  const handle_change = (
    content: string,
    _delta: unknown,
    source: 'user' | 'api' | 'silent'
  ) => {
    // 切换帮助项或外部回填值时，Quill 可能会触发一次非用户输入的 onChange。
    // 忽略 api/silent 更新，避免同一份 HTML 在重新解析时被额外写回空段落。
    if (source !== 'user') {
      return;
    }

    const normalized_content = content
      .replace(/<p><br><\/p>/gi, '')
      .replace(/<p>\s*<\/p>/gi, '')
      .trim();

    onChange?.(normalized_content);
  };

  return (
    <div className="simple-wysiwyg-editor">
      <style>{`
        .simple-wysiwyg-editor .ql-editor {
          line-height: 1.7;
        }

        .simple-wysiwyg-editor .ql-editor p,
        .simple-wysiwyg-editor .ql-editor h2,
        .simple-wysiwyg-editor .ql-editor h3,
        .simple-wysiwyg-editor .ql-editor blockquote,
        .simple-wysiwyg-editor .ql-editor ol,
        .simple-wysiwyg-editor .ql-editor ul,
        .simple-wysiwyg-editor .ql-editor li {
          margin-top: 0;
          margin-bottom: 0;
        }
      `}</style>
      <ReactQuill
        theme="snow"
        value={value || ''}
        onChange={handle_change}
        modules={modules}
        formats={formats}
        placeholder={placeholder || '请输入帮助内容'}
      />
    </div>
  );
};

export default SimpleWysiwygEditor;
